import AddIcon from "@mui/icons-material/Add";
import * as React from "react";

import { type SkillSummary } from "@/shared/domain/skills";
import { Button } from "@/shared/foundation/primitives";

import styles from "./AddSkillMenu.module.css";

interface AddSkillMenuProps {
  /** Org-shared skills available to attach — already filtered to exclude any
   *  the agent has selected, so picking one can never produce a duplicate. */
  options: SkillSummary[];
  /** True while the org skills query is still in flight. */
  loading: boolean;
  disabled?: boolean;
  onAdd: (skillId: string) => void;
}

/**
 * Plus-button menu that attaches one org-shared skill to the agent.
 *
 * The goals editor hardcodes its menu options because goal types are a
 * fixed, code-defined set; skills are org data, so the options here come
 * from the live ``useSkills`` query (filtered to the org-shared tiers,
 * minus the already-selected). Lives outside the skill list so the
 * affordance is present even when zero skills are attached.
 */
export function AddSkillMenu({
  options,
  loading,
  disabled,
  onAdd,
}: AddSkillMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // Close the menu if the trigger becomes disabled while open, so the popover
  // can't outlive its affordance.
  React.useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  // Reset the search field whenever the menu closes, so a stale filter
  // from a previous open doesn't hide options on reopen.
  React.useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (event: MouseEvent) => {
      if (
        anchorRef.current !== null &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Filter options on a normalized substring match against name *and* the
  // latest-version description; users typically remember a skill by what it
  // does rather than its exact name, so description is the better hit.
  const needle = search.trim().toLowerCase();
  const filteredOptions =
    needle === ""
      ? options
      : options.filter((option) => {
          const haystack = `${option.skill.name} ${
            option.latest_version?.description ?? ""
          }`.toLowerCase();
          return haystack.includes(needle);
        });

  return (
    <div className={styles.anchor} ref={anchorRef}>
      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Add skill
      </Button>
      {open && (
        <div className={styles.popover} role="menu" aria-label="Add skill">
          {/* Only show the search input when there's more than a couple of
              options — typing-to-filter a 2-row list is pure friction. The
              cutoff is intentionally low (>3) because skill libraries grow
              fast and an unexpected near-future state is a 20-row list. */}
          {!loading && options.length > 3 && (
            <input
              type="text"
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search skills…"
              aria-label="Search skills"
              autoFocus
            />
          )}
          {loading && (
            <span className={styles.empty}>Loading skills&hellip;</span>
          )}
          {!loading && options.length === 0 && (
            <span className={styles.empty}>
              No org-shared skills available to add.
            </span>
          )}
          {!loading && options.length > 0 && filteredOptions.length === 0 && (
            <span className={styles.empty}>No skills match your search.</span>
          )}
          {!loading &&
            filteredOptions.map((option) => (
              <button
                key={option.skill.skill_id}
                type="button"
                role="menuitem"
                className={styles.option}
                onClick={() => {
                  onAdd(option.skill.skill_id);
                  setOpen(false);
                }}
              >
                <span className={styles.optionLabel}>{option.skill.name}</span>
                <span className={styles.optionDescription}>
                  {option.latest_version?.description ??
                    "No description for the latest version."}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
